/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import { useGetQualifications } from "@src/appV2/Qualifications/api/useGetQualifications";
import { useOnboardingRequirement } from "@src/appV2/redesign/PlacementCandidate/hooks/useOnboardingRequirement";
import {
  PLACEMENT_COHORT,
  type PlacementCandidate,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { INTERVIEW_ATTENDANCE_STATUS } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useGetIncentives } from "../../../api/useGetIncentives";
import { type InterviewAvailability } from "../../../api/useGetInterviewAvailability";
import { useGetInterviewFromConnectedListing } from "../../../api/useGetInterviewFromConnectedListing";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { getHighestRankingQualification } from "../../../api/useHiringQualificationRanking";
import { useInterviewOnJobCards } from "../../../hooks/useInterviewOnJobCards";
import { usePlacementRecruiterScreeningConfig } from "../../../hooks/usePlacementRecruiterScreeningConfig";
import { useTalkToRecruiterCopyText } from "../../../hooks/useTalkToRecruiterCopyText";
import { useHiringIncentiveProgram } from "../../../useHiringIncentiveProgram";
import { useIsHiringReferralEnabled } from "../../../useIsHiringReferralEnabled";
import { useIsJobInterviewsEnabled } from "../../../useIsJobInterviewsEnabled";
import { useJobRequestConfiguration } from "../../../useJobRequestConfiguration";
import { hasAppliedToPlacement, interviewRequired, shouldShowRequirement } from "../../../utils";
import { buildPrimaryActionParams } from "../utils/buildPrimaryActionParams";
import { checkIsProfileCompletedForPlacement } from "../utils/checkIsProfileCompletedForPlacement";
import { getPrimaryAction } from "../utils/getPrimaryAction";
import { useBookingCohortEligibility } from "./useBookingCohortEligibility";
import { useInterviewSchedulingReliability } from "./useInterviewSchedulingReliability";
import { usePlacementInterviews } from "./usePlacementInterviews";

interface UsePlacementPrimaryActionParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  interviewAvailability: InterviewAvailability;
  isLoadingInterviewAvailability: boolean;
  maxReferralBonusAmount: number;
  isEasySchedulingPlacement: boolean;
}

/**
 * Pure data gathering and action selection logic.
 * No side effects, no state mutations.
 */
export function usePlacementPrimaryAction(params: UsePlacementPrimaryActionParams) {
  const {
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    maxReferralBonusAmount,
    isEasySchedulingPlacement,
  } = params;

  const { tmz: workerTimezoneRaw, userId: workerId, shoppableQualifications } = useDefinedWorker();
  const workerTimezone = workerTimezoneRaw ?? DEFAULT_TIMEZONE;
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();
  const autoApprovedQualifications = useAutoApprovedQualifications();
  const {
    data: interviewFromConnectedListing,
    isInitialLoading: isInterviewFromConnectedListingLoading,
  } = useGetInterviewFromConnectedListing({
    placement: placementDetails,
  });

  const { data: allQualificationsData, isLoading: isQualificationsLoading } = useGetQualifications({
    enabled: true,
  });
  const { hiringIncentiveProgram, incentiveProgram } = useHiringIncentiveProgram();
  const isHiringReferralEnabled = useIsHiringReferralEnabled();
  const { enabled: isJobRequestEnabled } = useJobRequestConfiguration(
    placementDetails.allowRequestInterview ?? false
  );
  const {
    isLoading: isOnboardingRequirementsLoading,
    isPending: isProfilePending,
    isInvalid: isProfileInvalid,
    isCompleted: isProfileCompleted,
    requirement,
  } = useOnboardingRequirement(workerId);
  const { data: signOnBonuses, isInitialLoading: isGetIncentivesLoading } = useGetIncentives(
    {
      workerId,
      queryParams: {
        filter: { sourceType: "Placement" },
      },
    },
    { enabled: hiringIncentiveProgram.enabled }
  );
  const talkToRecruiterCopy = useTalkToRecruiterCopyText();

  const isChildcareSupportChatEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_CHILDCARE_SUPPORT_CHAT, {
    defaultValue: false,
  });
  const isSubmitHiringDocumentsLaterEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_SUBMIT_HIRING_DOCUMENTS_LATER,
    { defaultValue: false }
  );
  const { cohorts, enabled: interviewsOnJobCardsEnabled } = useInterviewOnJobCards();
  const isInterviewsOnJobCardsCohort =
    interviewsOnJobCardsEnabled && cohorts.includes(PLACEMENT_COHORT.INTERVIEWS_ON_JOB_CARDS);

  const { calendlyLinks, showCallMeNow, legacyDocumentRequirementId } =
    usePlacementRecruiterScreeningConfig();

  const isRecruiterScreeningEnabled = isInterviewsOnJobCardsCohort
    ? false
    : (placementDetails.isRecruiterScreeningEnabled ?? false);

  const hasNonScreeningDocumentsMissing =
    (placementDetails.workerRequirements?.some((requirement) =>
      shouldShowRequirement(requirement, legacyDocumentRequirementId)
    ) ??
      false) ||
    (placementDetails.credentialRequirements?.some((requirement) =>
      shouldShowRequirement(requirement, legacyDocumentRequirementId)
    ) ??
      false);

  const { canBypassDocumentsForInterviewsCohort } = useBookingCohortEligibility({
    placementCandidate,
    isRecruiterScreeningEnabled,
    isJobInterviewsEnabled,
    requiredInterview: interviewRequired(placementDetails),
    hasNonScreeningDocumentsMissing,
    isInterviewsOnJobCardsCohort,
  });

  const { interview, attendedInterview, inProgressInterview, unverifiedBookedInterview } =
    usePlacementInterviews(placementDetails);
  const {
    workplaceStartHour,
    isReschedulingPastInterview,
    isLateCancellation,
    startsInWorkplaceToday,
    isSchedulingTodayAfterWorkplaceStartHour,
  } = useInterviewSchedulingReliability({
    interview,
    workplaceTimezone: placementDetails.workplace.tmz,
  });

  const highestRankingSharedQualification = getHighestRankingQualification(
    placementDetails.workerTypes ?? []
  );
  const isSignOnBonusPaid = signOnBonuses?.data.some((index) => index.attributes.status === "paid");
  const hasApplied = hasAppliedToPlacement(placementDetails);
  const isProfileCompletedOnPlacementDetailsPage = checkIsProfileCompletedForPlacement(
    isProfileCompleted,
    highestRankingSharedQualification,
    placementDetails.workerTypes,
    autoApprovedQualifications
  );
  const primaryActionParams = buildPrimaryActionParams(
    placementDetails,
    placementCandidate,
    interviewAvailability,
    {
      isLoadingInterviewAvailability,
      isProfileCompleted: isProfileCompletedOnPlacementDetailsPage,
      isProfilePending,
      isProfileInvalid,
      requirement,
      talkToRecruiterCopy,
      isJobInterviewsEnabled,
      isJobRequestEnabled,
      isHiringReferralEnabled,
      maxReferralBonusAmount,
      isOnboardingRequirementsLoading,
      isGetIncentivesLoading,
      isReschedulingPastInterview,
      startsInWorkplaceToday,
      bookedInterviewType: interview?.attributes.type,
      isChildcareSupportChatEnabled,
      isSubmitHiringDocumentsLaterEnabled,
      autoApprovedQualifications,
      workerShoppableQualificationIds:
        shoppableQualifications?.map((sq) => sq.relationships.qualification.data?.id) ?? [],
      allQualifications: allQualificationsData?.data ?? [],
      isQualificationsLoading,
      hasInProgressInterview: isDefined(inProgressInterview),
      hasWorkerManualEta: isDefined(inProgressInterview?.attributes.workerManualEta),
      isEtaUpdateEnabled: inProgressInterview?.meta?.isEtaUpdateEnabled ?? false,
      isWorkerDetectedAtWorkplace:
        inProgressInterview?.attributes.attendanceStatus ===
        INTERVIEW_ATTENDANCE_STATUS.WORKER_IN_LOCATION,
      isEasySchedulingPlacement,
      interviewFromConnectedListing,
      isInterviewFromConnectedListingLoading,
      canBypassDocumentsForInterviewsCohort,
      isRecruiterScreeningEnabled,
    }
  );

  const primaryAction = getPrimaryAction(primaryActionParams);

  const eventContext = {
    workerId,
    placementCandidateId: placementCandidate.id,
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    isProspectWorkplace: Boolean(placementDetails.workplace.placementSettings?.isProspect),
    placementScore: placementDetails.score,
    placementScoreBreakdown: placementDetails.scoreBreakdown,
  };

  return {
    primaryAction,
    primaryActionParams,
    derivedData: {
      workerId,
      workerTimezone,
      interview,
      attendedInterview,
      unverifiedBookedInterview,
      hasApplied,
      isSignOnBonusPaid,
      incentiveProgram,
      highestRankingSharedQualification,
      requirement,
      isProfilePending,
      isLateCancellation,
      isReschedulingPastInterview,
      workplaceStartHour,
      isSchedulingTodayAfterWorkplaceStartHour,
      startsInWorkplaceToday,
      eventContext,
      calendlyLinks,
      showCallMeNow,
      talkToRecruiterCopy,
      isJobRequestEnabled,
      isChildcareSupportChatEnabled,
      isSubmitHiringDocumentsLaterEnabled,
      missingAutoApprovedQualificationId: primaryActionParams.missingAutoApprovedQualificationId,
      missingCredentialRequirementType: primaryActionParams.missingCredentialRequirementType,
      inProgressInterview,
      hasInProgressInterview: isDefined(inProgressInterview),
      canBypassDocumentsForInterviewsCohort,
      isRecruiterScreeningEnabled,
      isInterviewsOnJobCardsCohort,
      connectedPlacementId: interviewFromConnectedListing?.placementId,
      hasConnectedListingInterview: primaryActionParams.hasConnectedListingInterview,
      isConnectedListingInterviewInOtherWorkplace:
        primaryActionParams.isConnectedListingInterviewInOtherWorkplace,
      legacyDocumentRequirementId,
    },
  };
}

export type PlacementPrimaryActionReturn = ReturnType<typeof usePlacementPrimaryAction>;
export type PlacementDerivedData = PlacementPrimaryActionReturn["derivedData"];
/* eslint-enable max-lines */
